import { useState } from 'react';

import {
	CheckboxAlign,
	CheckboxVerticalAlign,
	FontSizes,
	FontWeights,
	Loader,
	Text,
} from '@calm-web/design-system';

import AppleAuthButton from '@/components/authForm/AppleAuthButton';
import AuthTerms from '@/components/authForm/AuthTermsRefresh';
import EmailAuthButton from '@/components/authForm/EmailAuthButton';
import FacebookAuthButton from '@/components/authForm/FacebookAuthButton';
import GoogleAuthButton from '@/components/authForm/GoogleAuthButton';
import ModeToggle from '@/components/authForm/ModeToggleRefresh';
import PartnerSpecificAuthTerms from '@/components/SimplifiedSignup/Components/PartnershipSpecificAuthTerms';
import { useAuthPrompt } from '@/context/auth/AuthPromptProvider';
import { useAuth } from '@/context/auth/AuthProvider';
import { FormStateContextProvider } from '@/context/FormStateContextProvider';
import { usePartnerState, useRecaptchaVisibleState } from '@/hooks/store';

import { HeadingWrapper, SocialSignupWrapper, StyledMarketingOptIn, Wrapper } from './styles';
import { AuthFormProps } from './types';

const AuthForm = ({
	heading,
	onAuthSuccess,
	buttonText,
	loginTermsText,
	signupTermsText,
	partnerTerms,
	showMarketingOptIn = true,
	requiresDataSharing,
	loginHint,
	hideFacebookSignup,
	hideGoogleSignup,
	hideAppleSignup,
	hideAuthModeToggle,
	loaderColor,
	isDarkBackground,
}: AuthFormProps) => {
	const { isLoading } = useAuth();
	const recaptchaVisible = useRecaptchaVisibleState();
	const partner = usePartnerState();

	const [marketingOptIn, setMarketingOptIn] = useState(true);
	const { authPrompt } = useAuthPrompt();

	if (isLoading) {
		return <Loader color={loaderColor || 'gray8'} />;
	}

	return (
		<FormStateContextProvider>
			<Wrapper>
				<HeadingWrapper>
					{heading && (
						<Text noMargin el="h1" weight={FontWeights.Medium} size={FontSizes['2xl']}>
							{heading}
						</Text>
					)}
					{!hideAuthModeToggle && <ModeToggle />}
				</HeadingWrapper>
				<>
					<SocialSignupWrapper>
						<li>
							<EmailAuthButton
								buttonCTA={buttonText}
								contentAlignment="center"
								marketingOptIn={marketingOptIn}
								onAuthSuccess={onAuthSuccess}
								loginHint={loginHint}
								isDarkBackground={isDarkBackground}
							/>
						</li>
						{!hideFacebookSignup && (
							<li>
								<FacebookAuthButton
									contentAlignment="center"
									onAuthSuccess={onAuthSuccess}
									marketingOptIn={marketingOptIn}
								/>
							</li>
						)}
						{!hideAppleSignup && (
							<li>
								<AppleAuthButton
									contentAlignment="center"
									onAuthSuccess={onAuthSuccess}
									marketingOptIn={marketingOptIn}
								/>
							</li>
						)}
						{!hideGoogleSignup && (
							<li>
								<GoogleAuthButton
									contentAlignment="center"
									onAuthSuccess={onAuthSuccess}
									marketingOptIn={marketingOptIn}
								/>
							</li>
						)}
					</SocialSignupWrapper>
					{!requiresDataSharing && !recaptchaVisible && (
						<AuthTerms loginTermsText={loginTermsText} signupTermsText={signupTermsText as string} />
					)}
					{authPrompt === 'signup' && showMarketingOptIn && (
						<StyledMarketingOptIn
							onChange={value => setMarketingOptIn(value)}
							alignment={CheckboxAlign.Left}
							verticalAlignment={CheckboxVerticalAlign.Top}
							textSize={FontSizes.sm}
						/>
					)}
					{partner && (partnerTerms || requiresDataSharing) && (
						<PartnerSpecificAuthTerms terms={partnerTerms} requiresDataSharing={requiresDataSharing} />
					)}
				</>
			</Wrapper>
		</FormStateContextProvider>
	);
};

export default AuthForm;
